import { useEffect, useState } from "react"
import { adminApi, type HeroSlide } from "@/lib/admin-api"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog"
import { Plus, Pencil, Trash2, ImageIcon, Loader2, Upload, X, GripVertical } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

export default function AdminHeroSlides() {
    const [slides, setSlides] = useState<HeroSlide[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const [formModal, setFormModal] = useState<{ open: boolean; slide: HeroSlide | null }>({ open: false, slide: null })
    const [deleteModal, setDeleteModal] = useState<{ open: boolean; slide: HeroSlide | null }>({ open: false, slide: null })

    const [formData, setFormData] = useState({
        title: "",
        subtitle: "",
        button_text: "Xarid qilish",
        button_link: "/products",
        order: 0,
        active: true
    })
    const [imageFile, setImageFile] = useState<File | null>(null)
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const fetchSlides = async () => {
        try {
            setIsLoading(true)
            const data = await adminApi.getHeroSlides()
            setSlides(data)
        } catch (error) {
            toast.error("Slaydlarni yuklashda xatolik")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchSlides()
    }, [])

    const openFormModal = (slide: HeroSlide | null = null) => {
        if (slide) {
            setFormData({
                title: slide.title,
                subtitle: slide.subtitle || "",
                button_text: slide.button_text,
                button_link: slide.button_link,
                order: slide.order,
                active: slide.active
            })
            setImagePreview(slide.image_url)
        } else {
            setFormData({
                title: "",
                subtitle: "",
                button_text: "Xarid qilish",
                button_link: "/products",
                order: slides.length,
                active: true
            })
            setImagePreview(null)
        }
        setImageFile(null)
        setFormModal({ open: true, slide })
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setImageFile(file)
            setImagePreview(URL.createObjectURL(file))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formData.title) {
            toast.error("Sarlavha kiriting")
            return
        }
        if (!formModal.slide && !imageFile) {
            toast.error("Rasm yuklang")
            return
        }

        setIsSaving(true)
        try {
            const form = new FormData()
            form.append("title", formData.title)
            form.append("subtitle", formData.subtitle)
            form.append("button_text", formData.button_text)
            form.append("button_link", formData.button_link)
            form.append("order", String(formData.order))
            form.append("active", String(formData.active))
            if (imageFile) {
                form.append("image", imageFile)
            }

            if (formModal.slide) {
                await adminApi.updateHeroSlide(formModal.slide.id, form)
                toast.success("Slayd yangilandi")
            } else {
                await adminApi.createHeroSlide(form)
                toast.success("Slayd yaratildi")
            }
            setFormModal({ open: false, slide: null })
            fetchSlides()
        } catch (error: any) {
            toast.error(error.message || "Xatolik yuz berdi")
        } finally {
            setIsSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModal.slide) return
        setIsDeleting(true)
        try {
            await adminApi.deleteHeroSlide(deleteModal.slide.id)
            toast.success("Slayd o'chirildi")
            setDeleteModal({ open: false, slide: null })
            fetchSlides()
        } catch (error) {
            toast.error("O'chirishda xatolik")
        } finally {
            setIsDeleting(false)
        }
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Hero Slaydlar</h1>
                    <p className="text-muted-foreground">Bosh sahifa slider rasmlari</p>
                </div>
                <Button onClick={() => openFormModal()} className="bg-gradient-to-r from-emerald-500 to-teal-600 gap-2">
                    <Plus className="h-5 w-5" />
                    Yangi slayd
                </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {slides.map((slide) => (
                    <Card key={slide.id} className={cn("overflow-hidden", !slide.active && "opacity-50")}>
                        <div className="relative aspect-video">
                            {slide.image_url ? (
                                <img src={slide.image_url} alt={slide.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full bg-slate-100 dark:bg-gray-800 flex items-center justify-center">
                                    <ImageIcon className="h-12 w-12 text-muted-foreground" />
                                </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4 text-white">
                                <h3 className="font-bold text-lg">{slide.title}</h3>
                                {slide.subtitle && <p className="text-sm opacity-80">{slide.subtitle}</p>}
                            </div>
                            <div className="absolute top-2 right-2 flex gap-1">
                                <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => openFormModal(slide)}>
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="destructive" className="h-8 w-8" onClick={() => setDeleteModal({ open: true, slide })}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                            {!slide.active && (
                                <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                                    Nofaol
                                </div>
                            )}
                        </div>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Tartib: {slide.order}</span>
                                <span className="text-emerald-600">{slide.button_text} → {slide.button_link}</span>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {slides.length === 0 && (
                <Card className="p-12 text-center">
                    <ImageIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Hali slaydlar yo'q</p>
                    <Button onClick={() => openFormModal()} className="mt-4">
                        <Plus className="h-4 w-4 mr-2" />
                        Birinchi slaydni qo'shish
                    </Button>
                </Card>
            )}

            {/* Form Modal */}
            <Dialog open={formModal.open} onOpenChange={(open) => setFormModal({ open, slide: null })}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{formModal.slide ? "Slaydni tahrirlash" : "Yangi slayd"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Rasm *</Label>
                            {imagePreview ? (
                                <div className="relative">
                                    <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                                    <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2"
                                        onClick={() => { setImageFile(null); setImagePreview(null) }}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed rounded-lg cursor-pointer hover:border-emerald-500">
                                    <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                                    <span className="text-muted-foreground">Rasm yuklash (1920x600 tavsiya)</span>
                                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                                </label>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2 space-y-2">
                                <Label>Sarlavha *</Label>
                                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Asosiy sarlavha" />
                            </div>
                            <div className="col-span-2 space-y-2">
                                <Label>Qo'shimcha matn</Label>
                                <Input value={formData.subtitle} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} placeholder="Qisqa tavsif" />
                            </div>
                            <div className="space-y-2">
                                <Label>Tugma matni</Label>
                                <Input value={formData.button_text} onChange={(e) => setFormData({ ...formData, button_text: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Tugma havolasi</Label>
                                <Input value={formData.button_link} onChange={(e) => setFormData({ ...formData, button_link: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Tartib</Label>
                                <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })} />
                            </div>
                            <div className="flex items-center justify-between pt-6">
                                <Label>Faol</Label>
                                <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({ ...formData, active: checked })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setFormModal({ open: false, slide: null })}>Bekor</Button>
                            <Button type="submit" disabled={isSaving} className="bg-emerald-500">
                                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Saqlash
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Modal */}
            <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, slide: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Slaydni o'chirish</DialogTitle>
                        <DialogDescription>"{deleteModal.slide?.title}" ni o'chirmoqchimisiz?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteModal({ open: false, slide: null })}>Bekor</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}O'chirish
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
